export interface ThemeSetting {
  id: string;
  name: string;
  created_at: string;
  is_active: boolean;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  muted_color: string;
  destructive_color: string;
  background_color: string;
  foreground_color: string;
  border_color: string;
  card_color: string;
  dark_primary_color: string;
  dark_secondary_color: string;
  dark_accent_color: string;
  dark_muted_color: string;
  dark_destructive_color: string;
  dark_background_color: string;
  dark_foreground_color: string;
  dark_border_color: string;
  dark_card_color: string;
  font_family: string;
  font_size_base: string;
  border_radius: string;
  created_by?: string;
}

/** App-wide UI font — loaded from Google Fonts in index.html. */
export const APP_FONT_FAMILY = '"DM Sans", ui-sans-serif, system-ui, sans-serif';

/** Normalize legacy theme values to the current brand typeface. */
export function resolveFontFamily(fontFamily?: string | null): string {
  if (!fontFamily) return APP_FONT_FAMILY;
  if (fontFamily.includes("Inter") || fontFamily.includes("DM Sans")) return APP_FONT_FAMILY;
  return fontFamily;
}

export interface HSLColor {
  h: number;
  s: number;
  l: number;
}

/** Defaults aligned with tokens-portal-client.css (Dark Blue + neutrals). */
export const defaultLightTheme: Partial<ThemeSetting> = {
  primary_color: "207 56% 46%",
  secondary_color: "228 33% 97%",
  accent_color: "207 56% 92%",
  muted_color: "228 33% 97%",
  destructive_color: "0 72% 66%",
  background_color: "260 100% 99%",
  foreground_color: "234 19% 35%",
  border_color: "234 19% 91%",
  card_color: "0 0% 100%",
  font_family: APP_FONT_FAMILY,
  font_size_base: "1rem",
  border_radius: "0.75rem",
};

export const defaultDarkTheme: Partial<ThemeSetting> = {
  dark_primary_color: "207 56% 58%",
  dark_secondary_color: "217 19% 20%",
  dark_accent_color: "224 76% 15%",
  dark_muted_color: "217 19% 18%",
  dark_destructive_color: "0 70% 55%",
  dark_background_color: "222 47% 7%",
  dark_foreground_color: "210 40% 98%",
  dark_border_color: "217 19% 20%",
  dark_card_color: "222 47% 10%",
};

export const parseHSL = (hslString: string): HSLColor => {
  const parts = hslString.split(' ');
  return {
    h: parseInt(parts[0]) || 0,
    s: parseInt(parts[1]?.replace('%', '')) || 0,
    l: parseInt(parts[2]?.replace('%', '')) || 0
  };
};

export const formatHSL = (hsl: HSLColor): string => {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
};

export const hslToHex = (hsl: HSLColor): string => {
  const h = hsl.h;
  const s = hsl.s / 100;
  const l = hsl.l / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  
  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const hexToHSL = (hex: string): HSLColor => {
  let r = 0, g = 0, b = 0;
  
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  
  r /= 255; g /= 255; b /= 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
    }
  }
  
  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

export const applyThemeToDocument = (theme: Partial<ThemeSetting>, isDarkMode: boolean) => {
  const root = document.documentElement;
  
  if (isDarkMode) {
    /** Apply dark theme colors */
    if (theme.dark_primary_color) root.style.setProperty('--primary', theme.dark_primary_color);
    if (theme.dark_secondary_color) root.style.setProperty('--secondary', theme.dark_secondary_color);
    if (theme.dark_accent_color) root.style.setProperty('--accent', theme.dark_accent_color);
    if (theme.dark_muted_color) root.style.setProperty('--muted', theme.dark_muted_color);
    if (theme.dark_destructive_color) root.style.setProperty('--destructive', theme.dark_destructive_color);
    if (theme.dark_background_color) root.style.setProperty('--background', theme.dark_background_color);
    if (theme.dark_foreground_color) root.style.setProperty('--foreground', theme.dark_foreground_color);
    if (theme.dark_border_color) root.style.setProperty('--border', theme.dark_border_color);
    if (theme.dark_card_color) root.style.setProperty('--card', theme.dark_card_color);
  } else {
    /** Apply light theme colors */
    if (theme.primary_color) root.style.setProperty('--primary', theme.primary_color);
    if (theme.secondary_color) root.style.setProperty('--secondary', theme.secondary_color);
    if (theme.accent_color) root.style.setProperty('--accent', theme.accent_color);
    if (theme.muted_color) root.style.setProperty('--muted', theme.muted_color);
    if (theme.destructive_color) root.style.setProperty('--destructive', theme.destructive_color);
    if (theme.background_color) root.style.setProperty('--background', theme.background_color);
    if (theme.foreground_color) root.style.setProperty('--foreground', theme.foreground_color);
    if (theme.border_color) root.style.setProperty('--border', theme.border_color);
    if (theme.card_color) root.style.setProperty('--card', theme.card_color);
  }
  
  /** Apply typography and spacing */
  root.style.setProperty('--font-family', resolveFontFamily(theme.font_family));
  if (theme.font_size_base) root.style.setProperty('--font-size-base', theme.font_size_base);
  if (theme.border_radius) root.style.setProperty('--radius', theme.border_radius);
};

export const removeThemeFromDocument = () => {
  const root = document.documentElement;
  const properties = [
    '--primary', '--secondary', '--accent', '--muted', '--destructive',
    '--background', '--foreground', '--border', '--card',
    '--font-family', '--font-size-base', '--radius'
  ];
  properties.forEach(prop => root.style.removeProperty(prop));
};
